// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsStyle } from "@conklin/App/Components/Product/ProductActions";
import Button from "@conklin/MobiusOverrides/Button/Button";
import ProductBrand, { ProductBrandStyles } from "@conklin/Overrides/Components/ProductBrand";
import { parseBoolean } from "@conklin/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductSettingsModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing from "@insite/content-library/Components/ProductQuantityBreakPricing";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { css } from "styled-components";

interface Props {
    // products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    products: ProductContextModel[];
    doSearch: () => void;
    productSettings: ProductSettingsModel;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    header?: InjectableCss;
    headerLink?: LinkPresentationProps;
    imageClickable?: ClickablePresentationProps;
    actionsGridItem?: InjectableCss;
    productNumbersContainer?: InjectableCss;
    actions?: ProductActionsStyle;
    productPriceWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    brandStyles?: ProductBrandStyles;
    variantProductPriceLabel?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    // erpNumberText: {
    //     ellipsis: true,
    //     css: css`
    //         width: 270px;
    //         text-align: left;
    //         margin: 5px 0 0 0;
    //     `,
    // },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products, productSettings } = this.props;
        const styles = this.styles;
        return (
            <>
                <StyledWrapper {...styles.header}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Products")} ({products.length})
                    </Typography>
                    {/** XNG-Changes */}
                    {products.length > 1 && (
                        <Link
                            onClick={() => {
                                this.props.doSearch();
                            }}
                            {...styles.headerLink}
                        >
                            {translate("View All")}
                        </Link>
                    )}
                </StyledWrapper>
                {/** XNG-Changes */}
                {products.map(productContext => {
                    const {
                        product: {
                            id,
                            customerProductNumber,
                            productNumber,
                            manufacturerItem,
                            productTitle,
                            mediumImagePath,
                            detail,
                            brand,
                            properties,
                        },
                        productInfo: { productDetailPath },
                    } = productContext;

                    return (
                        <ProductContext.Provider value={productContext} key={id!}>
                            <StyledWrapper
                                {...(this.props.focusedItem?.id === id ? styles.focusedContainer : styles.container)}
                                className="autocomplete-products-container"
                            >
                                <Clickable
                                    onClick={() => {
                                        this.props.goToUrl(productDetailPath);
                                    }}
                                    {...styles.imageClickable}
                                >
                                    <LazyImage
                                        width="72"
                                        height="72"
                                        {...styles.image}
                                        src={mediumImagePath}
                                        className="autocomplete-products-image"
                                        altText={`autocomplete products for ${productTitle}`}
                                    />
                                </Clickable>
                                <StyledWrapper
                                    {...styles.infoGridItem}
                                    className="autocomplete-products-info-grid-item"
                                >
                                    {brand?.name && (
                                        <ProductBrand
                                            brand={brand}
                                            redirectToPlp={true}
                                            extendedStyles={styles.brandStyles}
                                        />
                                    )}
                                    <Link
                                        {...styles.titleLink}
                                        onClick={() => {
                                            this.props.goToUrl(productDetailPath);
                                        }}
                                        aria-label={productTitle}
                                    >
                                        {productTitle}
                                    </Link>
                                    <StyledWrapper className="product-numbers" {...styles.productNumbersContainer}>
                                        {productNumber && (
                                            <SmallHeadingAndText
                                                heading={translate("Item Code")}
                                                text={productNumber}
                                                extendedStyles={styles.erpNumberText}
                                            />
                                        )}
                                        {properties?.sku && detail?.sku && (
                                            <SmallHeadingAndText
                                                heading={translate("SKU")}
                                                text={detail?.sku}
                                                extendedStyles={styles.erpNumberText}
                                            />
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper {...styles.actionsGridItem}>
                                    <StyledWrapper {...styles.productPriceWrapper}>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${productContext.productInfo.productId}-uom`}
                                            id={`${productContext.productInfo.productId}-uom-label`}
                                        >
                                            {translate("U/M")}
                                        </VisuallyHidden>
                                        <ProductPrice
                                            showLabel={false}
                                            product={productContext}
                                            extendedStyles={styles.productPriceStyles}
                                        />
                                        <ProductQuantityBreakPricing />
                                    </StyledWrapper>
                                    {parseBoolean(productContext.product.properties?.hasVariants) ? (
                                        <Button
                                            onClick={() => {
                                                this.props.goToUrl(productDetailPath);
                                            }}
                                            sizeVariant="small"
                                            typographyProps={{
                                                size: 11,
                                                lineHeight: "13px",
                                            }}
                                        >
                                            {translate("Choose Selection")}
                                        </Button>
                                    ) : (
                                        <ProductActions
                                            extendedStyles={styles.actions}
                                            showQty={true}
                                            hideUOM={false}
                                            iconOnly={
                                                productSettings.alternateUnitsOfMeasure &&
                                                productContext.product.unitOfMeasures &&
                                                productContext.product.unitOfMeasures.filter(uom => uom.unitOfMeasure)
                                                    .length > 1
                                            }
                                        />
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </ProductContext.Provider>
                    );
                })}
            </>
        );
    }
}

export default AutocompleteProducts;
